/**
 * Which status an order may move to next, and who is allowed to move it.
 *
 * The archive is the one screen that can move an order *backwards* — a
 * cancelled order back to delivered, a delivered order back into the kitchen —
 * so the gates here are the difference between correcting a mistake and
 * rewriting the day's takings. They are ported from legacy `pages/lists.tsx`
 * unchanged, and pinned by tests because BACKEND.md §14.5 says money moves.
 */

/** Statuses that cannot be set without somebody's credentials and a reason. */
export const PROTECTED_STATUSES = ['delivered', 'quality_check', 'cancelled']

/**
 * The transitions the archive offers on a row. Deliberately not a state
 * machine: `cancelled → delivered` exists because a cancellation taken by
 * mistake has to be reversible, and `delivered → quality_check` exists because
 * a complaint arrives after the food does.
 */
export const STATUS_TRANSITIONS: Record<string, string[]> = {
  new: ['preparing'],
  preparing: ['ready'],
  ready: ['on_the_way', 'delivered', 'quality_check', 'cancelled'],
  on_the_way: ['delivered', 'quality_check', 'cancelled'],
  delivered: ['quality_check', 'cancelled'],
  quality_check: ['delivered', 'cancelled'],
  cancelled: ['delivered', 'quality_check'],
}

/** Everything a `main_admin` may force from the override dropdown. */
export const ALL_STATUSES = [
  'new',
  'preparing',
  'ready',
  'quality_check',
  'on_the_way',
  'at_address',
  'delivered',
  'cancelled',
]

interface TransitionPermissions {
  /** `change_order_status` — without it the row offers nothing at all. */
  canChangeStatus: boolean
  /** `cancel_order` — held separately, so a waiter may close but not void. */
  canCancel: boolean
}

/**
 * The buttons a row shows. Two independent permissions, and the narrower one
 * only ever subtracts: someone who may change a status but not cancel sees the
 * same list with `cancelled` removed, not a different list.
 */
export function availableTransitions(
  status: string | undefined,
  { canChangeStatus, canCancel }: TransitionPermissions,
): string[] {
  if (!canChangeStatus) return []
  const all = STATUS_TRANSITIONS[status ?? ''] ?? []
  return canCancel ? all : all.filter((next) => next !== 'cancelled')
}

/**
 * A finished order can be sent back to the kitchen, but only from a state where
 * it really did finish. `picked_up` is in the list even though no other screen
 * in this module sets it — the driver app writes it, and a collected order that
 * never arrived is precisely the case this button exists for.
 */
export const REOPENABLE_STATUSES = ['delivered', 'picked_up']

/**
 * BACKEND.md §14.5: re-opening a delivered order clears the driver and deducts
 * their wallet credit. So the gate is deliberately narrow — the two admin roles
 * always, and anyone else only with the explicit `return_to_kitchen` flag. A
 * head waiter is given it; a driver is not, because a driver would be voting on
 * their own pay.
 */
export function canReturnToKitchen({
  systemRole,
  hasPermission,
  status,
}: {
  systemRole: string | undefined
  hasPermission: (permission: string) => boolean
  status: string | undefined
}): boolean {
  const roleAllows =
    systemRole === 'main_admin' || systemRole === 'admin' || hasPermission('return_to_kitchen')
  return roleAllows && REOPENABLE_STATUSES.includes(status ?? '')
}
